// Consumer-app analytics — reuses the existing (already-built) admin analytics
// endpoints under /admin/analytics and /admin/analytics/aggregated.

import { apiGet } from './client';

function dateRange(days: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date(end.getTime() - days * 86400000);
  // Full ISO timestamps: a date-only endDate parses as midnight UTC and
  // silently excludes everything that happened today.
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export interface DashboardOverview {
  keyMetrics: {
    totalEvents: number;
    eventGrowth: string;
    uniqueDevices: number;
    deviceGrowth: string;
    eventsPerDevice: string;
  };
  topEvents: { event: string; count: number }[];
  dailyTrend: { date: string; events: number; devices: number }[];
}

export interface ScanStats {
  totalScans: number;
  uniqueDevices: number;
  // The API returns these as object maps (label → count), not arrays.
  methodBreakdown?: Record<string, number>;
  dailyBreakdown?: Record<string, number>;
  hourlyBreakdown?: Record<string, number>;
  // As encoded on the document (raw AAMVA value: COMPLIANT | NON_COMPLIANT |
  // NOT_ENCODED | NOT_REPORTED). Same raw-event sourcing and 7-day window as
  // methodBreakdown — events without the metadata key are omitted, not
  // zeroed. Optional — older API builds won't send it.
  realIdBreakdown?: Record<string, number>;
}

export interface SessionStats {
  totalSessions: number;
  uniqueDevices: number;
  averageDuration: number;
  totalDuration: number;
}

export interface FunnelStep {
  event: string;
  label: string;
  devices: number;
  pctOfPrevious: number | null;
  pctOfFirst: number | null;
}

// Wire shape of /admin/analytics/aggregated/funnels (verified live): device
// co-occurrence funnels — step N counts devices that emitted every prior
// step's event too, so percentages are <=100 by construction. `launched`
// false means the funnel's entry event has never fired (Business pre-launch).
export interface FunnelResult {
  key: string;
  launched: boolean;
  steps: FunnelStep[];
  context?: { event: string; label: string; devices: number }[];
}

export interface Cohort {
  cohortDate: string;
  cohortSize: number;
  retention: {
    daysSinceFirstSeen: number;
    returningDevices: number;
    retentionRate: string;
  }[];
}

export interface SubscriptionBreakdown {
  breakdown: {
    type: 'free' | 'pro' | 'business';
    users: number;
    events: number;
    userPercentage: string;
    eventPercentage: string;
  }[];
}

export type DailySeriesMetric =
  | 'activeDevices'
  | 'sessions'
  | 'scans'
  | 'newDevices'
  | 'proPurchases';

export interface DailySeries {
  metric: string;
  points: { date: string; value: number }[];
}

// Wire shape of /admin/analytics/aggregated/freshness. See that endpoint's
// doc comment (API src/analytics/aggregation.controller.ts) for why this
// exists: DailyEventAggregation is written by a 02:00 UTC cron covering
// *yesterday*, so it can look up to two days stale even when ingest is
// perfectly healthy — this is how the console tells those two apart.
export interface AnalyticsFreshness {
  newestRawEventAt: string | null;
  newestDailyAggregationDate: string | null;
  newestHourlyAggregation: { date: string; hour: number } | null;
  rawEventCountLast24h: number;
  nextDailyAggregationRunAt: string;
}

export const getDailySeries = (
  metric: DailySeriesMetric,
  days: number,
): Promise<DailySeries> =>
  apiGet<DailySeries>('/admin/analytics/aggregated/daily-series', {
    metric,
    ...dateRange(days),
  });

export const getDashboardOverview = (days: number): Promise<DashboardOverview> =>
  apiGet<DashboardOverview>(
    '/admin/analytics/aggregated/dashboard-overview',
    dateRange(days),
  );

export const getScanStats = (days: number): Promise<ScanStats> =>
  apiGet<ScanStats>('/admin/analytics/scan-stats', dateRange(days));

export const getSessionStats = (days: number): Promise<SessionStats> =>
  apiGet<SessionStats>('/admin/analytics/session-stats', dateRange(days));

export const getFunnels = (days: number): Promise<FunnelResult[]> =>
  apiGet<FunnelResult[]>('/admin/analytics/aggregated/funnels', dateRange(days));

export const getCohorts = (days: number): Promise<Cohort[]> =>
  apiGet<Cohort[]>('/admin/analytics/aggregated/cohorts', dateRange(days));

export const getSubscriptionBreakdown = (
  days: number,
): Promise<SubscriptionBreakdown> =>
  apiGet<SubscriptionBreakdown>(
    '/admin/analytics/aggregated/subscription-breakdown',
    dateRange(days),
  );

export const getAnalyticsFreshness = (): Promise<AnalyticsFreshness> =>
  apiGet<AnalyticsFreshness>('/admin/analytics/aggregated/freshness');

export interface LiveAnalyticsOverview {
  generatedAt: string;
  window: { start: string; end: string; hours: number };
  totals: {
    events: number;
    activeDevices: number;
    scans: number;
    scanningDevices: number;
    sessions: number;
    onboardingCompletions: number;
    unidentifiedEvents: number;
    latestEventAt: string | null;
  };
  versions: {
    appVersion: string;
    buildNumber: string;
    platform: string;
    activeDevices: number;
    deviceShare: number;
    events: number;
    scans: number;
    latestEventAt: string;
  }[];
  scanMethods: { method: string; scans: number }[];
}

export const getLiveAnalytics = (hours: number): Promise<LiveAnalyticsOverview> =>
  apiGet<LiveAnalyticsOverview>('/admin/analytics/live', { hours });
